import React from 'react';
import { Box, Typography, TextField, InputAdornment } from '@mui/material';
import { colors, fonts, spacing, borderRadius } from '../../constants';

/**
 * Shared profile field styles
 */
const getProfileFieldStyles = (isEditing, isDisabled) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: borderRadius.input,
    fontSize: fonts.fontSizeSm,
    backgroundColor: !isEditing || isDisabled ? colors.backgroundSoft : 'transparent',
    '& fieldset': {
      borderColor: !isEditing || isDisabled ? colors.borderHover : colors.borderDefault,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: isEditing && !isDisabled ? colors.borderHover : colors.borderHover,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primaryDark,
      borderWidth: '2px',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.backgroundSoft,
      opacity: 1,
      '& fieldset': {
        borderColor: colors.borderHover,
        borderWidth: '1px',
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
    fontSize: fonts.fontSizeSm,
    color: colors.textPrimaryDark,
    cursor: isDisabled ? 'not-allowed' : 'text',
    '&::placeholder': {
      color: colors.textSecondary,
      opacity: 1,
    },
  },
});

/**
 * ProfileField Component
 * Reusable profile field component
 */
const ProfileField = ({
  label,
  icon: Icon,
  value,
  onChange,
  disabled = false,
  placeholder,
  type = 'text',
  InputProps,
  error,
  errorText,
  helperText,
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: fonts.fontSizeSm,
          color: colors.textPrimaryDark,
          fontWeight: fonts.weightSemiBold,
          marginBottom: spacing.xs,
          letterSpacing: fonts.letterSpacingNormal,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
        }}
      >
      {Icon && <Icon sx={{ color: colors.primaryDark, fontSize: fonts.fontSizeSm }} />}
        {label}
      </Typography>
      <TextField
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        fullWidth
        size="small"
        error={error}
        InputProps={{
          startAdornment: Icon ? (
            <InputAdornment position="start">
              <Icon sx={{ color: value ? colors.primaryDark : colors.textSecondary, fontSize: fonts.fontSizeSm }} />
            </InputAdornment>
          ) : undefined,
          ...InputProps,
        }}
        sx={{
          ...getProfileFieldStyles(!disabled, disabled),
          '& .MuiOutlinedInput-root': {
            ...getProfileFieldStyles(!disabled, disabled)['& .MuiOutlinedInput-root'],
            '&.Mui-error fieldset': {
              borderColor: colors.error,
            },
          },
        }}
      />
      {error && errorText ? (
        <Typography
          sx={{
            fontSize: fonts.fontSizeXs,
            color: colors.error,
            mt: spacing.xs,
          }}
        >
          {errorText}
        </Typography>
      ) : helperText ? (
        <Typography
          sx={{
            fontSize: fonts.fontSizeXs,
            color: colors.textSecondary,
            mt: spacing.xs,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ProfileField;
